import { useAchievementTypes, useReduxState } from 'hooks';
import { LinkButton, MoreOptions } from 'components/common';
import { UsersSendMessage } from 'components/users';
import {
  AchievementCompletionsAddUser,
  AchievementCompletionsRemove,
  AchievementCompletionsUpdate,
  AchievementGroupOfUsersAdd
} from '.';

import type { AchievementData, UserWithBasicData } from 'shared-types';

interface Props {
  isSupervisorPage?: boolean;
  supervisedUserIds?: number[];
  targetUser: UserWithBasicData & { achievementData: AchievementData };
  updateUsers: () => Promise<void>;
}

const AchievementUsersActions = ({
  isSupervisorPage = false,
  supervisedUserIds = [],
  targetUser,
  updateUsers
}: Props) => {
  const {
    messages,
    pageState: { achievement },
    serverConfig: { adminUrl }
  } = useReduxState();

  const { hasStatus, isGroupAchievement } = useAchievementTypes({
    type: achievement?.type
  });

  // disable removal if achievement was added through a path and has not been started
  const addedBy = targetUser.achievementData?.addedBy;

  const isRemovingDisabled =
    targetUser.achievementData?.status === 'not started' &&
    addedBy &&
    typeof addedBy !== 'string' &&
    addedBy.addedByType === 'path';

  const isPathUserWithoutCompletion =
    targetUser.achievementData &&
    typeof targetUser.achievementData.addedBy === 'object' &&
    'addedByType' in targetUser.achievementData.addedBy &&
    targetUser.achievementData.addedBy.addedByType === 'path' &&
    !targetUser.achievementData.id;

  const doNotShowActions =
    !isSupervisorPage &&
    !achievement.isOwnerOrSuperadmin &&
    !achievement.userAdminPermissions.includes('manage-users');

  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        gap: 10,
        justifyContent: 'flex-end'
      }}
    >
      {targetUser.achievementData && !targetUser.isAnonymous ? (
        <>
          {achievement.type === 'conversation' ? (
            <LinkButton
              category="lighter"
              href={`/admin/achievements/${achievement.id}/users/${targetUser.id}`}
              icon="comments"
              size="header"
              tooltip={messages.achievementsMarkOrUpdateAnswers}
            />
          ) : achievement.type === 'tasklist' ? (
            <LinkButton
              category="lighter"
              href={`/admin/achievements/${achievement.id}/users/${targetUser.id}`}
              icon="clipboard-list"
              size="header"
              tooltip={messages.achievementsMarkOrUpdateTasks}
            />
          ) : null}
        </>
      ) : null}

      {doNotShowActions ? null : (
        <>
          {(targetUser.achievementData?.status === 'not started' &&
            !hasStatus) ||
          (isSupervisorPage && !targetUser.achievementData) ? (
            isGroupAchievement ? (
              <AchievementGroupOfUsersAdd
                isSupervisorPage={true}
                supervisedUserIds={supervisedUserIds}
                updateGroups={updateUsers}
                user={targetUser}
              />
            ) : (
              <AchievementCompletionsAddUser
                user={targetUser}
                updateUsers={updateUsers}
              />
            )
          ) : null}

          <MoreOptions
            category="lighter"
            renderInPortal={true}
            styleContentButtons={false}
          >
            <UsersSendMessage
              buttonIsDropdown={true}
              emailButtonTarget={`${adminUrl}/achievements/${achievement.id}`}
              id="achievement-users-actions-send-message"
              userSelectionParams={{
                selectedUserIds: [targetUser.id],
                userSelector: 'emails'
              }}
            />

            {targetUser.achievementData &&
            !(
              targetUser.achievementData?.status === 'not started' && !hasStatus
            ) ? (
              <>
                {!targetUser.isAnonymous ? (
                  <AchievementCompletionsUpdate
                    isDropdown={true}
                    isUserWithoutCompletion={isPathUserWithoutCompletion}
                    user={targetUser}
                    updateUsers={updateUsers}
                  />
                ) : null}

                {targetUser.achievementData?.status === 'completed' ? (
                  <AchievementCompletionsAddUser
                    user={targetUser}
                    userHasPreviousCompletions={true}
                    updateUsers={updateUsers}
                  />
                ) : null}

                <AchievementCompletionsRemove
                  disabled={isRemovingDisabled}
                  hasMultipleCompletions={
                    targetUser.achievementData?.numberOfPreviousCompletions > 0
                  }
                  updateUsers={updateUsers}
                  userId={targetUser.id}
                />
              </>
            ) : null}
          </MoreOptions>
        </>
      )}
    </div>
  );
};

export default AchievementUsersActions;
